import { useMemo } from 'react'
import { CheckCircle } from '@/ui/icons'
import { useMealNeeds } from '@/modules/meals/hooks/useMealNeeds'
import { useGearNeeds } from '@/modules/gear/hooks/useGearNeeds'
import { SectionCard } from './SectionCard'
import { cn } from '@/shared/utils'

const TOTAL_TASKS = 6

export type ChecklistItemId =
  | 'radio'
  | 'details'
  | 'itinerary'
  | 'meals'
  | 'gear'
  | 'crew'

interface TripReadinessSectionProps {
  tripId: string
  trip: {
    radio_channel?: string | null
    start_date?: string | null
    end_date?: string | null
    description?: string | null
  }
  waypointCount: number
  memberCount: number
  onChecklistItemClick?: (itemId: ChecklistItemId) => void
  className?: string
}

interface TaskItem {
  id: ChecklistItemId
  label: string
  completed: boolean
}

export function TripReadinessSection({
  tripId,
  trip,
  waypointCount,
  memberCount,
  onChecklistItemClick,
  className
}: TripReadinessSectionProps) {
  const { meals } = useMealNeeds(tripId, { skip: false })
  const { gearNeeds } = useGearNeeds(tripId)

  const hasRadioChannel = Boolean(trip.radio_channel)
  const hasTripDetails = Boolean(trip.start_date && trip.end_date && trip.description)
  const hasItinerary = waypointCount > 0
  const hasMealPlan = (meals?.length ?? 0) > 0
  const hasGearChecklist = (gearNeeds?.length ?? 0) > 0
  const hasInviteCrew = memberCount > 1

  const checklist: TaskItem[] = useMemo(
    () => [
      { id: 'radio', label: 'Radio Channel', completed: hasRadioChannel },
      { id: 'details', label: 'Trip Details', completed: hasTripDetails },
      { id: 'itinerary', label: 'Itinerary', completed: hasItinerary },
      { id: 'meals', label: 'Meal Plan', completed: hasMealPlan },
      { id: 'gear', label: 'Gear Checklist', completed: hasGearChecklist },
      { id: 'crew', label: 'Invite Crew', completed: hasInviteCrew }
    ],
    [
      hasRadioChannel,
      hasTripDetails,
      hasItinerary,
      hasMealPlan,
      hasGearChecklist,
      hasInviteCrew
    ]
  )

  const completedCount = useMemo(
    () => checklist.filter((item) => item.completed).length,
    [checklist]
  )
  const progressPercent = (completedCount / TOTAL_TASKS) * 100

  return (
    <SectionCard
      title="Trip Readiness"
      count={completedCount}
      className={cn('min-w-0', className)}
      contentClassName="pt-2"
      fixedHeight
    >
      <div className="flex items-center gap-2 text-sm text-earth-600 dark:text-earth-400 mb-2">
        <div className="flex-1 h-1.5 rounded-full bg-earth-200 dark:bg-earth-700 overflow-hidden">
          <div
            className="h-full bg-adventure-500 rounded-full transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="tabular-nums flex-shrink-0 text-xs">
          {completedCount}/{TOTAL_TASKS}
        </span>
      </div>
      <ul className="space-y-0.5">
        {checklist.map((item) => {
          const isActionable = Boolean(onChecklistItemClick)
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={isActionable ? () => onChecklistItemClick?.(item.id) : undefined}
                disabled={!isActionable}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  isActionable
                    ? 'text-foreground hover:bg-earth-100 dark:hover:bg-earth-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-1'
                    : 'cursor-default'
                )}
                aria-label={
                  isActionable
                    ? item.completed
                      ? `${item.label}, complete. Click to edit.`
                      : `${item.label}, incomplete. Click to set up.`
                    : undefined
                }
              >
                <span className="flex-shrink-0 text-earth-500 dark:text-earth-400">
                  {item.completed ? (
                    <CheckCircle className="h-4 w-4 text-success" />
                  ) : (
                    <span className="h-4 w-4 rounded-full border-2 border-earth-300 dark:border-earth-600 inline-block" />
                  )}
                </span>
                <span className="flex-1 min-w-0 truncate font-medium">{item.label}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </SectionCard>
  )
}
